import { useState } from "react";
import api from "@/lib/api";

import { toast } from "sonner";
import { ArrowUpRight } from "lucide-react";
import { locationTypes } from "../data/homepageData";

import { executeRecaptcha } from "@/recaptcha";
import { Checkbox } from "@/components/ui/checkbox";
import LegalPostModal from "@/components/LegalsModal";

export default function ContactForm({ publicConfig }) {
  const [form, setForm] = useState({
    nume_complet: "",
    phone: "",
    email: "",
    location_type: "",
    location: "",
    mesaj: "",
  });

  const [termsAccepted, setTermsAccepted] = useState(false);
  const [legalModalOpen, setLegalModalOpen] = useState(false);
  const [legalSlug, setLegalSlug] = useState("");
  const [legalTitle, setLegalTitle] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  const updateField = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const openLegalModal = (slug, title) => {
    setLegalSlug(slug);
    setLegalTitle(title);
    setLegalModalOpen(true);
  };

  const closeLegalModal = () => {
    setLegalModalOpen(false);
    setLegalSlug("");
    setLegalTitle("");
  };

  const validate = () => {
    if (!form.nume_complet.trim()) {
      toast.error("Completează numele.");
      return false;
    }

    if (!form.phone.trim()) {
      toast.error("Completează numărul de telefon.");
      return false;
    }

    if (!/^\+?[0-9\s()-]{8,}$/.test(form.phone.trim())) {
      toast.error("Număr de telefon invalid.");
      return false;
    }

    if (!form.email.trim()) {
      toast.error("Completează adresa de email.");
      return false;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) {
      toast.error("Adresă de email invalidă.");
      return false;
    }

    if (!form.location.trim()) {
      toast.error("Completează numele businessului.");
      return false;
    }

    if (!form.location_type.trim()) {
      toast.error("Selectează tipul de business.");
      return false;
    }

    if (!termsAccepted) {
      toast.error("Trebuie să accepți termenii și condițiile.");
      return false;
    }

    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validate()) return;

    const enabled = !!publicConfig?.recaptcha?.enabled;
    const sitekey = publicConfig?.recaptcha?.sitekey;

    let recaptchaToken = null;
    if (enabled && sitekey) {
      try {
        recaptchaToken = await executeRecaptcha(sitekey, "contact_submit");
      } catch (error) {
        console.error("reCAPTCHA failed", error);
      }

      if (!recaptchaToken) {
        toast.error("Nu am putut valida reCAPTCHA. Reîncarcă pagina și încearcă din nou.");
        return;
      }
    }

    setIsSubmitting(true);

    try {
      const payload = {
        nume_complet: form.nume_complet.trim(),
        telefon: form.phone.trim(),
        email: form.email.trim(),
        tip_locatie: form.location_type,
        nume_locatie: form.location.trim(),
        mesaj: form.mesaj.trim(),
        website: "",
        recaptcha_token: recaptchaToken,
        recaptcha_action: enabled && sitekey ? "contact_submit" : null,
      };

      await api.post("/contact", payload);

      toast.success("Solicitarea a fost trimisă.");
      setForm({
        nume_complet: "",
        phone: "",
        email: "",
        location_type: "",
        location: "",
        mesaj: "",
      });
      setTermsAccepted(false);
    } catch (error) {
      console.error("Lead submit failed", error);
      toast.error(error?.response?.data?.mesaj || "A apărut o eroare la trimitere.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <div className="rz-form-card" id="lead-form">
        <div className="rz-form-card__header">
          <span>Contact rapid</span>
          <h2>Solicită o prezentare și îți propunem varianta potrivită pentru businessul tău</h2>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="rz-form-grid">
            <label>
              <input
                type="text"
                value={form.nume_complet}
                onChange={(e) => updateField("nume_complet", e.target.value)}
                placeholder="Numele tău complet"
              />
            </label>

            <label>
              <input
                type="tel"
                value={form.phone}
                onChange={(e) => updateField("phone", e.target.value)}
                placeholder="Număr de telefon"
              />
            </label>
          </div>

          <div className="rz-form-grid">
            <label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => updateField("email", e.target.value)}
                placeholder="Email"
              />
            </label>

            <label>
              <input
                type="text"
                value={form.location}
                onChange={(e) => updateField("location", e.target.value)}
                placeholder="Numele businessului"
              />
            </label>
          </div>

          <label>
            <span>Tip business</span>
            <select
              value={form.location_type}
              onChange={(e) => updateField("location_type", e.target.value)}
            >
              <option value="">Selectează</option>
              {locationTypes.map((item) => (
                <option key={item} value={item}>
                  {item}
                </option>
              ))}
            </select>
          </label>

          <label>
            <textarea
              rows="4"
              value={form.mesaj}
              onChange={(e) => updateField("mesaj", e.target.value)}
              placeholder="Spune-ne pe scurt ce tip de rezervări sau programări vrei să gestionezi și dacă ai nevoie de varianta Basic, Pro sau cu plată online."
            />
          </label>

          <div className="rz-checkbox-row">
            <Checkbox
              id="termsAccepted"
              checked={termsAccepted}
              onCheckedChange={setTermsAccepted}
              className="rz-checkbox-control"
            />

            <div className="rz-checkbox-text">
              <span> Sunt de acord cu </span>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openLegalModal("termeni-si-conditii", "Termeni și condiții");
                }}
                className="rz-checkbox-link"
              >
                &nbsp; termenii și condițiile
              </button>
              <span className="rz-checkbox-required"> *</span>
            </div>
          </div>

          <button type="submit" className="rz-submit" disabled={isSubmitting}>
            {isSubmitting ? "Se trimite..." : "Solicită prezentarea"}
            <ArrowUpRight size={18} />
          </button>

          <p className="rz-form-note">
            Durează mai puțin de 30 de secunde. Revenim cu o propunere personalizată, fără obligații.
          </p>
        </form>
      </div>

      <LegalPostModal
        open={legalModalOpen}
        slug={legalSlug}
        fallbackTitle={legalTitle}
        onClose={closeLegalModal}
      />
    </>
  );
}
